import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import AppApiService from '../../ApiServices/WebsiteApiService/AppApiService';
import { useAuth } from "../Auth/AuthContext";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from "framer-motion";
import TableTabs from './TableTabs';
import SearchBar from './SearchBar';
import AddButton from './AddButton';
import WebDataTable from './WebDataTable';
import AppDataTable from './AppDataTable';
import RejectionModal from './RejectionModal';
import LoadingIndicator from './LoadingIndicator';
import AddWebsiteModalForm from './AddWebsiteModelForm';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const WebsiteTable = () => {
    const [openModal, setOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [rejectionFile, setRejectionFile] = useState(null);
    const [webData, setWebData] = useState([]);
    const [webFilteredData, setWebFilteredData] = useState([]);
    const [appData, setAppData] = useState([]);
    const [appFilteredData, setAppFilteredData] = useState([]);
    const [activeTable, setActiveTable] = useState("Website");
    const [editType, setEditType] = useState(null);
    const [currentEditItem, setCurrentEditItem] = useState(null);

    const Auth = useAuth();
    const user = Auth.authState.user;
    const { authState } = useAuth();
    const role = authState.roles;
    const admin = role === "Admin";

    const status = {
        GettingReady: "Getting Ready",
        Approved: "Approved",
        Disapproved: "Disapproved"
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const websiteData = admin === true ? await WebsiteApiService.Get() : await WebsiteApiService.GetAllWebByUserId(user.id);
            const appData = admin === true ? await AppApiService.Get() : await AppApiService.GetAllAppByUserId(user.id);

            const websiteDataArray = Array.isArray(websiteData.data) ? websiteData.data : [websiteData.data];
            const appDataArray = Array.isArray(appData.data) ? appData.data : [appData.data];

            const websiteDataWithStatus = websiteDataArray.map((item) => ({
                Name: item.Name || item.name,
                Title: item.Title || item.title,
                Url: item.Url || item.url,
                Description: item.Description || item.description,
                CreatedOn: new Date(item.CreatedOn || item.createdOn).toISOString().split('T')[0],
                ApprovalStatus: item.readableStatus,
                status: 'Website',
                URL: item.url,
                PublisherName: admin == true ? item.user.firstName + " " + item.user.lastName : "",
                WebsiteId: item.WebsiteId || item.websiteId,
                IsAttachmentAvailable: item.isAttachmentAvailable
            }));

            const appDataWithStatus = appDataArray.map((item) => ({
                Name: item.Name || item.name,
                Title: item.Title || item.title,
                Url: item.Url || item.url,
                Description: item.Description || item.description,
                CreatedOn: new Date(item.CreatedOn || item.createdOn).toISOString().split('T')[0],
                ApprovalStatus: item.readableStatus,
                status: 'App',
                PublisherName: admin == true ? item.user.firstName + " " + item.user.lastName : "",
                Id: item.Id || item.id,
                AppId: item.AppId || item.appId,
                IsAttachmentAvailable: item.isAttachmentAvailable
            }));

            setWebFilteredData(websiteDataWithStatus || []);
            setWebData(websiteDataWithStatus);
            setAppData(appDataWithStatus);
            setAppFilteredData(appDataWithStatus || []);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const changeWebsiteStatus = async (event, index) => {
        const newStatus = event.target.value;
        let selectedItem = webData.filter(x => x.WebsiteId == index)[0];
        setCurrentItem(selectedItem);
        let id = selectedItem.WebsiteId;

        var obj = {
            "status": newStatus,
            "id": id
        };

        toast.info("Please wait for a while to update status");
        await WebsiteApiService.ChangeStatus(obj);
        toast.success("Status updated");
        await fetchData();
    };

    const changeAppStatus = async (event, index) => {
        const newStatus = event.target.value;
        let selectedItem = appData.filter(x => x.Id == index)[0];
        setCurrentItem(selectedItem);
        let id = selectedItem.Id;

        let model = {
            id: id,
            status: newStatus
        };

        await AppApiService.ChangeStatus(model);
        toast.success("Status updated");
        await fetchData();
    };

    const handleRejectionStatus = async () => {
        const formData = new FormData();
        formData.append("status", status.Disapproved);
        formData.append("rejectionReason", rejectionReason);

        if (rejectionFile) {
            formData.append("RejectionAttachment", rejectionFile);
        }

        if (activeTable == "Website") {
            formData.append("id", currentItem.WebsiteId);
            toast.info("Please wait for a while to update status");
            await WebsiteApiService.ChangeStatus(formData);
            toast.success("Web Rejected");
        } else {
            formData.append("id", currentItem.Id);
            await AppApiService.ChangeStatus(formData);
            toast.success("App Rejected");
        }

        setRejectionReason("");
        setIsRejectionModalOpen(false);
        await fetchData();
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const activeData = activeTable === "Website" ? webData : appData;
        const filteredResults = activeData.filter((item) =>
            Object.values(item).some((val) =>
                val?.toString().toLowerCase().includes(value)
            )
        );
        activeTable === "Website" ? setWebFilteredData(filteredResults) : setAppFilteredData(filteredResults);
    };

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
        fetchData();
    }, []);
    // Add this function to your WebsiteTable component
    const handleDelete = async () => {
        if (!currentItem) {
            toast.error("Invalid item selected!");
            return;
        }

        try {
            if (currentItem.status === "Website") {
                await WebsiteApiService.Delete(currentItem.WebsiteId);
                toast.success("Website deleted successfully!");
            } else if (currentItem.status === "App") {
                await AppApiService.Delete(currentItem.Id);
                toast.success("App deleted successfully!");
            }
            setIsDeleteModalOpen(false);
            await fetchData(); // Refresh data after deletion
        } catch (error) {
            console.error("Error deleting item:", error);
            toast.error(error.message || "Failed to delete the item!");
        }
    };
    return (
        <motion.div
            className="overflow-x-auto p-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <motion.h1
                className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-6 border-b-4 pb-2 border-indigo-200 w-fit"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
            >
                🌐 Web / 📱 App Details
            </motion.h1>

            <div className="flex justify-between items-center mb-6">
                <TableTabs
                    activeTable={activeTable}
                    setActiveTable={setActiveTable}
                />
                <AddButton
                    activeTable={activeTable}
                    setOpenModal={setOpenModal}
                />
            </div>

            <SearchBar
                searchTerm={searchTerm}
                handleSearch={handleSearch}
            />

            {isLoading ? (
                <LoadingIndicator activeTable={activeTable} />
            ) : (
                <>
                    {activeTable === "Website" && (
                        <WebDataTable
                            webFilteredData={webFilteredData}
                            admin={admin}
                            status={status}
                            changeWebsiteStatus={changeWebsiteStatus}
                            DownloadWebRejectionAttachment={WebsiteApiService.DownloadRejectionAttachment}
                            openEditModal={(item) => {
                                setEditType("Website");
                                setCurrentEditItem(item);
                                setIsEditing(true);
                            }}
                            openDeleteModal={(item) => {
                                setCurrentItem(item);
                                setIsDeleteModalOpen(true);
                            }}
                        />
                    )}

                    {activeTable === "App" && (
                        <AppDataTable
                            appFilteredData={appFilteredData}
                            admin={admin}
                            status={status}
                            changeAppStatus={changeAppStatus}
                            DownloadAppRejectionAttachment={AppApiService.DownloadRejectionAttachment}
                            openEditModal={(item) => {
                                setEditType("App");
                                setCurrentEditItem(item);
                                setIsEditing(true);
                            }}
                            openDeleteModal={(item) => {
                                setCurrentItem(item);
                                setIsDeleteModalOpen(true);
                            }}
                        />
                    )}
                </>
            )}

            {isRejectionModalOpen && (
                <RejectionModal
                    rejectionReason={rejectionReason}
                    setRejectionReason={setRejectionReason}
                    rejectionFile={rejectionFile}
                    setRejectionFile={setRejectionFile}
                    setIsRejectionModalOpen={setIsRejectionModalOpen}
                    handleRejectionStatus={handleRejectionStatus}
                    fetchData={fetchData}
                />
            )}

            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    itemType={activeTable}
                    onConfirm={handleDelete}
                    onCancel={() => setIsDeleteModalOpen(false)}
                />
            )}

            <AddWebsiteModalForm
                isOpen={openModal}
                closeModal={() => setOpenModal(false)}
            />
        </motion.div>
    );
};

export default WebsiteTable;